import React, { useCallback, useState } from "react";
import DateConversion from "../../../../../utils/DateConversion";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import { Checkbox } from "@mui/material";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { updateOngoingHardwareParts } from "../../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { markHardwarePartComplete, markNotHardwarePartComplete } from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { fetchHardwareProjectTracking } from "../../../../../store/slices/hardwareDescriptionSlice";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";

const OpTaskTrackHardware = ({ matchedHardwarePartsOP, row, setIsLoading, managerSelfPartIdsByAssemblyDescription = {}, isViewOnly = false }) => {
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
    "L4_PM"
  );

  const [updatedHardwareEntries, setUpdatedHardwareEntries] = useState({});
  const [loadingRows, setLoadingRows] = useState({});

  const handleHardwareQuantityUpdate = async (entryId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    if (!entryId) return;

    const entryData = updatedHardwareEntries[entryId];
    if (!entryData) return;

    const updatedData = {
      quantity: entryData.quantity,
      totalprice: entryData.totalprice,
    };
    console.log("handleHardwareQuantityUpdate entryId", entryId, updatedData);
    try {
      await dispatch(
        updateOngoingHardwareParts({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch (error) {
      enqueueSnackbar("Failed to update quantity", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelfEntryHardware = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      syncToolGuideStep("view-details");
      const matchedEntry = updatedHardwareEntries[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      const updatedData = {
        description: entry.assemblepartdescription,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };

      try {
        await dispatch(
          updateOngoingHardwareParts({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
      } finally {
        setIsLoading(false);
      }
    },
    [
      updatedHardwareEntries,
      row?._id,
      dispatch,
      enqueueSnackbar,
      setIsLoading,
      isViewOnly,
      syncToolGuideStep,
    ]
  );

  const handleDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedHardwareEntries((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDateSelfEntryHardware(newentry);
      }
    },
    [handleDateSelfEntryHardware, isViewOnly]
  );

  const handleHardwarePartsMarkComplete = async (entry) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markHardwarePartComplete({ projectid, taskid }));
      // When manager completes an operation person's part, also complete the manager's self task for the same part (hidden in "Self" when manager has lower quantity)
      const managerPartIds = managerSelfPartIdsByAssemblyDescription[entry?.assemblepartdescription] || [];
      for (const managerTaskId of managerPartIds) {
        if (managerTaskId && managerTaskId !== taskid) {
          await dispatch(markHardwarePartComplete({ projectid, taskid: managerTaskId }));
        }
      }
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonCompleteHardware = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNotHardwarePartComplete({ projectid, taskid }));
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
        <div className="z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
          <div className="p-1 border w-[11%] flex justify-center items-center">
            Part Name
          </div>
          <div className="p-0.5 border w-[10%] flex justify-center items-center">
            Part No
          </div>
          <div className="p-0.5 border w-[10%] flex justify-center items-center">
            Quantity
          </div>
          <div className="p-0.5 border w-[11%] flex justify-center items-center">
            Total Price ({currency})
          </div>
          <div className="p-0.5 border w-[14%] flex justify-center items-center">
            Start Date
          </div>
          <div className="p-0.5 border w-[14%] flex justify-center items-center">
            End Date
          </div>
          <div className="p-0.5 border w-[24%] flex justify-center items-center">
            Tracker
          </div>
          <div className="p-0.5 border w-[6%] flex justify-center items-center">
            Action
          </div>
        </div>

        <div className="flex text-[10px] break-all whitespace-normal max-h-14">
          <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center align-middle">
            {"Total"}
          </div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[10%] flex justify-center items-center"></div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[10%] flex justify-center items-center align-middle">
            {USAComma(
              matchedHardwarePartsOP.reduce(
                (sum, item) => sum + (item?.quantity || 0),
                0
              )
            )}{" "}
          </div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center align-middle">
            {USAComma(
              matchedHardwarePartsOP.reduce(
                (sum, item) => sum + (item?.totalprice || 0),
                0
              )
            )}
          </div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
          <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
            {(() => {
              const allEntries = matchedHardwarePartsOP.length;
              const completedEntriesCount = matchedHardwarePartsOP.filter(
                (entry) => entry.isTaskComplete
              ).length;
              const trackerPercentage =
                allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
              return <ProgressTracker value={trackerPercentage} />;
            })()}
          </div>
          <div className="p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center"></div>
        </div>

        {matchedHardwarePartsOP.length > 0 ? (
          matchedHardwarePartsOP.map((entry, index) => (
            <div
              key={entry}
              className="flex text-[10px] break-all whitespace-normal max-h-14"
            >
              <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center align-middle">
                {entry.assemblepartdescription}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center items-center align-middle">
                {entry.partno}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[10%] flex justify-center items-center align-middle">
                {/* {entry.quantity || ""} */}
                {entry.isTaskComplete ? (
                  <span>{entry.quantity || ""}</span>
                ) : (
                  <input
                    type="text"
                    disabled={isViewOnly}
                    value={
                      updatedHardwareEntries[entry._id]?.quantity ??
                      entry.quantity ??
                      ""
                    }
                    placeholder="enter quantity"
                    className={`border border-gray-200 w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    onChange={(e) => {
                      const qty = Number(e.target.value) || 0;

                      setUpdatedHardwareEntries((prev) => ({
                        ...prev,
                        [entry._id]: {
                          ...prev[entry._id],
                          quantity: qty,
                          totalprice: qty * entry.unitprice,
                        },
                      }));
                    }}
                    onBlur={() => handleHardwareQuantityUpdate(entry._id)}
                  />
                )}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center align-middle">
                {/* {USAComma(entry.totalprice)} */}
                {USAComma(
                  updatedHardwareEntries[entry._id]?.totalprice ??
                    entry.totalprice ??
                    0
                )}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center align-middle">
                {/* {entry.startdate ? formatDateByCountry(entry.startdate) : ""}{" "} */}
                {entry.isTaskComplete ? (
                  <div className="text-black">
                    {entry.startdate
                      ? formatDateByCountry(entry.startdate)
                      : ""}
                  </div>
                ) : (
                  <DatePicker
                    dateFormat={getDateFormat()}
                    className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                    placeholderText={getDatePlaceholder()}
                    popperClassName="z-[1500]"
                    minDate={
                      entry.startdate
                        ? new Date(entry.startdate)
                        : row?.DateRequested
                          ? new Date(row.DateRequested)
                          : null
                    }
                    maxDate={new Date()}
                    selected={
                      updatedHardwareEntries[entry._id]?.startdate
                        ? updatedHardwareEntries[entry._id].startdate
                        : entry.startdate
                        ? new Date(entry.startdate)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange(date, "startdate", entry)
                    }
                  />
                )}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center align-middle">
                {/* {entry.enddate ? formatDateByCountry(entry.enddate) : ""} */}
                {entry.isTaskComplete ? (
                  <div className="text-black">
                    {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                  </div>
                ) : (
                  <DatePicker
                    dateFormat={getDateFormat()}
                    className="w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white"
                    placeholderText={getDatePlaceholder()}
                    popperClassName="z-[1500]"
                    minDate={
                      // ✅ Use updated startdate if available, else fallback to entry.startdate
                      updatedHardwareEntries[entry._id]?.startdate
                        ? new Date(updatedHardwareEntries[entry._id].startdate)
                        : entry.startdate
                        ? new Date(entry.startdate)
                        : null
                    }
                    selected={
                      updatedHardwareEntries[entry._id]?.enddate
                        ? updatedHardwareEntries[entry._id].enddate
                        : entry.enddate
                        ? new Date(entry.enddate)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange(date, "enddate", entry)
                    }
                  />
                )}
              </div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center ">
                <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
              </div>
              <div className={` p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center cursor-default 
                  ${
                    row.Assignedperson.every(
                      (person) => person.isProjectComplete === true
                    )
                      ? "cursor-default"
                      : ""
                  }
                  `}
                title={`${entry.isTaskComplete ? "Completed" : "Pending"}`}
              >
                {/* <Checkbox
                  disabled={row.Assignedperson?.every(
                    (person) => person.isProjectComplete === true
                  )}
                  className="cursor-default pointer-events-none"
                  readOnly
                  checked={entry.isTaskComplete}
                /> */}
                {loadingRows[index] ? (
                  <LoadingSpinner />
                ) : (
                  <Checkbox
                    checked={entry.isTaskComplete}
                    disabled={
                      isViewOnly ||
                      row.Assignedperson?.every(
                        (person) => person.isProjectComplete === true
                      )
                    }
                    sx={
                      (() => {
                        const isProjectSubmitted = row.Assignedperson?.every(
                          (person) => person.isProjectComplete === true
                        );
                        if (isViewOnly || isProjectSubmitted) {
                          return {
                            color: "grey.500 !important",
                            "&.Mui-checked": { 
                              color: "grey.600 !important",
                            },
                            "&.Mui-disabled": {
                              color: "grey.500 !important",
                            },
                            "&.Mui-checked.Mui-disabled": {
                              color: "grey.600 !important",
                            }
                          };
                        }
                        return undefined;
                      })()
                    }
                    onChange={(e) => {
                      if (isViewOnly) return;
                      const isChecked = e.target.checked;
                      const startDate =
                        updatedHardwareEntries[entry._id]?.startdate ||
                        entry.startdate;
                      const endDate =
                        updatedHardwareEntries[entry._id]?.enddate ||
                        entry.enddate;
                      if (isChecked) {
                        if (!startDate || !endDate) {
                          enqueueSnackbar("Please Select End Date", {
                            variant: "error",
                          });
                          return;
                        }
                        handleHardwarePartsMarkComplete(entry, index);
                        handleDateSelfEntryHardware(entry);
                      } else {
                        handleMarkNonCompleteHardware(entry);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
            <p className="text-red-500 text-center">
              {" "}
              No Tracking Task Entries Found
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpTaskTrackHardware;
